import { useState } from 'react';

import { PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useReportDetailUi } from '@/modules/analytics/hooks/useReportDetailUi';
import { useCreateReport } from '@/modules/analytics/hooks/useReports';
import { trackAnalyticsReportCreated } from '@/modules/analytics/utils/analyticsTracking';
import { selectAccountSlug } from '@/redux/session/selectors';
import { PageName } from '@/shared/types/segment';

import { ReportNameForm } from './ReportNameForm';

export const CreateReportPopover = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const slug = useSelector(selectAccountSlug);
  const navigate = useNavigate();
  const { openAddMetric } = useReportDetailUi();
  const { mutate: createReport, isPending } = useCreateReport();

  const handleSubmit = (name: string) => {
    createReport(
      { name },
      {
        onSuccess: (created) => {
          trackAnalyticsReportCreated(PageName.ANALYTICS_CUSTOM_REPORTS, created.report_id);
          setOpen(false);
          openAddMetric(created.report_id);
          navigate(`/${slug}/analytics/reports/${created.report_id}`);
        },
      }
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <InfoTooltip content={t('analytics.reports_create')} side="top">
        <PopoverTrigger asChild>
          <Button variant="ghost-neutral" size="icon-sm" aria-label={t('analytics.reports_create')}>
            <PlusIcon size={16} />
          </Button>
        </PopoverTrigger>
      </InfoTooltip>
      <PopoverContent align="start" className="w-80 p-4">
        <ReportNameForm
          title={t('analytics.reports_create')}
          submitLabel={t('common.create')}
          isSubmitting={isPending}
          onCancel={() => setOpen(false)}
          onSubmit={handleSubmit}
        />
      </PopoverContent>
    </Popover>
  );
};
